import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { trackEvent, planPrice } from "@/components/Analytics";

export const PENDING_KEY = "aicom_pending_idea";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  const [pendingIdea, setPendingIdea] = useState(null);

  const writeIdea = () => {
    sessionStorage.removeItem(PENDING_KEY);
    navigate(pendingIdea.route, { state: { topic: pendingIdea.topic } });
  };

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 6) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
          if (pending) setPendingIdea(pending); trackEvent("abonnement_koebt", { trial: false, currency: "DKK", value: planPrice(data.plan) }); setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative px-6">
      <div className="grain" />
      <div className="relative z-10 text-center max-w-md" data-testid="payment-status">
        {status === "checking" && (<><Loader2 className="h-10 w-10 animate-spin mx-auto text-muted-foreground mb-6" /><h1 className="font-display text-2xl font-medium">Bekræfter din betaling...</h1></>)}
        {status === "paid" && (<><CheckCircle2 className="h-12 w-12 mx-auto text-foreground mb-6" /><h1 className="font-display text-3xl font-medium">Tak — I er opgraderet!</h1><p className="text-muted-foreground mt-3">Jeres nye plan er aktiv med det samme og fornyes automatisk hver måned. I kan til enhver tid opsige under Indstillinger.</p>{pendingIdea ? (
          <div className="mt-8 flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground" data-testid="payment-pending-idea">Klar til at skrive: <span className="text-foreground">{pendingIdea.title}</span></p>
            <Button onClick={writeIdea} data-testid="payment-write-idea-btn">Skriv udkast nu</Button>
          </div>
        ) : (
          <Button className="mt-8" onClick={() => navigate("/dashboard")} data-testid="payment-continue-btn">Til dashboard</Button>
        )}</>)}
        {["error", "expired", "timeout"].includes(status) && (<><XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-6" /><h1 className="font-display text-2xl font-medium">Betalingen blev ikke gennemført</h1><p className="text-muted-foreground mt-3">Prøv igen, eller kontakt os hvis problemet fortsætter.</p><Button className="mt-8" variant="outline" onClick={() => navigate("/priser")} data-testid="payment-retry-btn">Tilbage til planer</Button></>)}
      </div>
    </div>
  );
}
